import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    setPagination,
    setSort,
    searchCategories,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination
} from '../../store/modules/category/categoryActions';
import CategoryForm from '../../components/pageModule/Categories/CategoryForm';
import { useHasPermission } from '../../utils/permissionUtils';

const MySwal = withReactContent(Swal);

const Categories = ({ excludedActions = [], onSelectedRowsChange = () => { } }) => {

    const hasCreatePermission = useHasPermission(['create categories']);
    const hasUpdatePermission = useHasPermission(['update categories']);
    const hasViewPermission = useHasPermission(['view categories']);

    const categoryState = useSelector(state => state.category);
    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createCategory(formData));
            toast.success('Category created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating category', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateCategory(id, formData));
            toast.success('Category updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating category', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteCategory(id));
                toast.success('Category deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting category', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name?.en ?? '-',
            sortable: true,
            sortField: 'name',
        },
        {
            name: 'Status',
            width: '100px',
            selector: row => row.is_active ?
                <span className="badge bg-success">Active</span> :
                <span className="badge bg-danger">Inactive</span>,
        },
    ];


    const actions = {
        fetch: (queryFilter, activeFilter) => dispatch(fetchCategories(queryFilter, activeFilter)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchCategories(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter))
    };

    let defaultAvailableActions = ['search'];
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission || hasUpdatePermission) {
        defaultAvailableActions = ['actions', 'edit', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Categories"
                moduleName="Category"
                moduleKey="categories"
                state={categoryState}
                actions={actions}
                FormComponent={CategoryForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                selectableRows={false}
                onSelectedRowsChange={onSelectedRowsChange}
            />
        </>
    );
};

export default Categories; 